import React, { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import FAQ from "../components/FAQ";
import SEO from "../components/SEO";
import { Check, ArrowRight, Sparkles, Minus } from "lucide-react";

const plans = [
  {
    name: "Starter",
    tagline: "For lean teams testing the waters.",
    monthly: 499,
    annual: 399,
    cta: "Start Free Trial",
    features: [
      "Up to 5,000 visitor IDs / month",
      "Up to 10,000 consumer records",
      "Audience Builder (basic)",
      "5 native integrations",
      "Email support",
      "Beacon Score on all records",
    ],
    notIncluded: ["Direct mail activation", "Dedicated strategist", "API access"],
    highlight: false,
  },
  {
    name: "Growth",
    tagline: "For growth teams ready to scale outbound.",
    monthly: 1499,
    annual: 1199,
    cta: "Get a Demo",
    features: [
      "Up to 50,000 visitor IDs / month",
      "Up to 250,000 consumer records",
      "Audience Builder (advanced)",
      "All 200+ native integrations",
      "Priority chat & email support",
      "Direct mail activation",
      "Beacon Pulse (real-time signals)",
      "API & webhook access",
    ],
    notIncluded: ["Dedicated strategist"],
    highlight: true,
    badge: "Most Popular",
  },
  {
    name: "Authority",
    tagline: "For agencies, resellers, and high-volume teams.",
    monthly: null,
    annual: null,
    cta: "Talk to Sales",
    features: [
      "Unlimited visitor IDs",
      "Unlimited consumer & B2B records",
      "Custom Beacon Score thresholds",
      "White-label / co-brand option",
      "Dedicated account strategist",
      "Onboarding & team training",
      "SLA-backed uptime",
      "Authorized Affiliate program access",
    ],
    notIncluded: [],
    highlight: false,
  },
];

const comparison = [
  { label: "Visitor identification", starter: "5K/mo", growth: "50K/mo", authority: "Unlimited" },
  { label: "Consumer records", starter: "10K", growth: "250K", authority: "Unlimited" },
  { label: "Native integrations", starter: "5", growth: "200+", authority: "200+" },
  { label: "Beacon Score", starter: true, growth: true, authority: true },
  { label: "Beacon Pulse (real-time)", starter: false, growth: true, authority: true },
  { label: "Direct mail activation", starter: false, growth: true, authority: true },
  { label: "API & webhooks", starter: false, growth: true, authority: true },
  { label: "Dedicated strategist", starter: false, growth: false, authority: true },
  { label: "White-label option", starter: false, growth: false, authority: true },
  { label: "Affiliate program access", starter: false, growth: false, authority: true },
];

export default function PricingPage() {
  const [annual, setAnnual] = useState(true);

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Pricing — BuyerBeacon Pro"
        description="Three plans for BuyerBeacon Pro: Starter ($499), Growth ($1,499), and Scale. Pay for results, not seats. Annual plans save 20%."
        path="/pricing"
      />
      <Navbar />
      <main className="pt-28">
        {/* Hero */}
        <section className="relative overflow-hidden pb-10 lg:pb-14">
          <div className="pointer-events-none absolute left-1/2 top-[60px] -z-10 h-[320px] w-[640px] -translate-x-1/2 rounded-full bg-[#d4af37]/12 blur-[120px]" />
          <div className="mx-auto max-w-7xl px-5 lg:px-8">
            <div className="text-center">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                Pricing
              </p>
              <h1 className="mx-auto mt-3 max-w-3xl text-5xl font-medium tracking-tight text-[#1e1b4b] sm:text-6xl">
                Pay for results. <span className="text-slate-400">Not seats.</span>
              </h1>
              <p className="mx-auto mt-5 max-w-2xl text-[16px] leading-relaxed text-slate-600">
                Three plans, scaled to where your team is today. Upgrade when
                your pipeline does — never before.
              </p>
            </div>

            {/* billing toggle */}
            <div className="mt-10 flex items-center justify-center gap-3">
              <span className={`text-sm ${!annual ? "font-semibold text-[#1e1b4b]" : "text-slate-500"}`}>
                Monthly
              </span>
              <button
                onClick={() => setAnnual((v) => !v)}
                className={`relative h-6 w-11 rounded-full transition-colors ${
                  annual ? "bg-[#1e1b4b]" : "bg-slate-300"
                }`}
                aria-label="Toggle billing"
              >
                <span
                  className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${
                    annual ? "translate-x-5" : "translate-x-0.5"
                  }`}
                />
              </button>
              <span className={`text-sm ${annual ? "font-semibold text-[#1e1b4b]" : "text-slate-500"}`}>
                Annual
              </span>
              <span className="ml-1 rounded-full bg-[#d4af37]/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-[#7c5e0a]">
                Save 20%
              </span>
            </div>
          </div>
        </section>

        {/* Plan cards */}
        <section className="pb-16">
          <div className="mx-auto grid max-w-6xl grid-cols-1 gap-5 px-5 lg:grid-cols-3 lg:px-8">
            {plans.map((p) => (
              <div
                key={p.name}
                className={`relative flex flex-col rounded-2xl border bg-white p-7 transition-all hover:-translate-y-1 hover:shadow-[0_24px_50px_-20px_rgba(30,27,75,0.18)] ${
                  p.highlight
                    ? "border-[#1e1b4b] shadow-[0_24px_60px_-20px_rgba(30,27,75,0.35)]"
                    : "border-slate-200"
                }`}
              >
                {p.badge && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-[#d4af37] px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-[#1e1b4b]">
                    {p.badge}
                  </span>
                )}
                <h3 className="text-xl font-semibold text-[#1e1b4b]">{p.name}</h3>
                <p className="mt-1 text-sm text-slate-500">{p.tagline}</p>

                <div className="mt-6 flex items-baseline gap-1">
                  {p.monthly === null ? (
                    <>
                      <span className="text-4xl font-medium tracking-tight text-[#1e1b4b]">Custom</span>
                    </>
                  ) : (
                    <>
                      <span className="text-4xl font-medium tracking-tight text-[#1e1b4b]">
                        ${annual ? p.annual : p.monthly}
                      </span>
                      <span className="text-sm text-slate-500">/ mo</span>
                    </>
                  )}
                </div>
                {p.monthly !== null && (
                  <p className="mt-1 text-xs text-slate-500">
                    {annual ? "billed annually" : "billed monthly"}
                  </p>
                )}

                <Link
                  to={p.name === "Authority" ? "/demo" : "/demo"}
                  className={`mt-6 inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm font-semibold transition-all ${
                    p.highlight
                      ? "bg-[#1e1b4b] text-white hover:bg-[#1e1b4b]/92"
                      : "border border-slate-300 bg-white text-[#1e1b4b] hover:bg-slate-50"
                  }`}
                >
                  {p.cta}
                  <ArrowRight className="h-4 w-4" />
                </Link>

                <div className="mt-7 border-t border-slate-100 pt-6">
                  <ul className="flex flex-col gap-3 text-sm">
                    {p.features.map((f) => (
                      <li key={f} className="flex items-start gap-2.5 text-[#1e1b4b]">
                        <span className="mt-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-[#d4af37]/15">
                          <Check className="h-3 w-3 text-[#7c5e0a]" />
                        </span>
                        {f}
                      </li>
                    ))}
                    {p.notIncluded.map((f) => (
                      <li key={f} className="flex items-start gap-2.5 text-slate-400 line-through">
                        <span className="mt-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-slate-100">
                          <Minus className="h-3 w-3" />
                        </span>
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Comparison table */}
        <section className="border-t border-slate-200/80 bg-slate-50/40 py-20">
          <div className="mx-auto max-w-6xl px-5 lg:px-8">
            <div className="text-center">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                Compare Plans
              </p>
              <h2 className="mx-auto mt-3 max-w-2xl text-4xl font-medium tracking-tight text-[#1e1b4b]">
                Everything, side by side.
              </h2>
            </div>

            <div className="mt-12 overflow-x-auto rounded-2xl border border-slate-200 bg-white">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50/60 text-[11px] uppercase tracking-wider text-slate-500">
                    <th className="px-5 py-4 text-left font-semibold">Feature</th>
                    <th className="px-5 py-4 text-center font-semibold">Starter</th>
                    <th className="px-5 py-4 text-center font-semibold text-[#1e1b4b]">Growth</th>
                    <th className="px-5 py-4 text-center font-semibold">Authority</th>
                  </tr>
                </thead>
                <tbody>
                  {comparison.map((row, i) => (
                    <tr key={row.label} className={i % 2 ? "bg-slate-50/40" : ""}>
                      <td className="px-5 py-4 font-medium text-[#1e1b4b]">{row.label}</td>
                      {["starter", "growth", "authority"].map((k) => {
                        const v = row[k];
                        return (
                          <td key={k} className="px-5 py-4 text-center text-slate-700">
                            {v === true ? (
                              <Check className="mx-auto h-4 w-4 text-[#7c5e0a]" />
                            ) : v === false ? (
                              <Minus className="mx-auto h-4 w-4 text-slate-300" />
                            ) : (
                              <span className="font-mono text-xs">{v}</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Affiliate teaser */}
        <section className="border-t border-slate-200/80 bg-white py-20">
          <div className="mx-auto max-w-5xl px-5 lg:px-8">
            <div className="overflow-hidden rounded-3xl border border-slate-200 bg-gradient-to-br from-[#faf8f3] to-white p-10 sm:p-14">
              <div className="flex flex-col items-start gap-6 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#d4af37]">
                    <Sparkles className="h-3.5 w-3.5" />
                    Authorized Affiliate
                  </div>
                  <h3 className="mt-3 text-3xl font-medium tracking-tight text-[#1e1b4b]">
                    Are you an agency or reseller?
                  </h3>
                  <p className="mt-3 max-w-xl text-[15px] text-slate-600">
                    Earn meaningful commissions and unlock white-label access
                    through the BuyerBeacon Affiliate program.
                  </p>
                </div>
                <Link
                  to="/affiliate"
                  className="group inline-flex items-center gap-2 whitespace-nowrap rounded-xl bg-[#1e1b4b] px-5 py-3 text-sm font-semibold text-white hover:bg-[#1e1b4b]/92"
                >
                  Learn More
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </Link>
              </div>
            </div>
          </div>
        </section>

        <FAQ />
      </main>
      <Footer />
    </div>
  );
}
